import type {
  ArenaState, Candle, CreatorView, CurveSnapshot, DrawRecord, IndexerHealth,
  LeaderboardRow, PeriodView, TokenSummary, Trade,
} from '@colosseum/shared';
import {config} from './config.js';

/**
 * Typed reads against the indexer. Every call can fail; callers are expected to
 * cope, because the indexer being down should degrade the site rather than
 * break it.
 */

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

async function get<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${config.apiUrl}/api${path}`, {
    signal: AbortSignal.timeout(10_000),
    ...init,
  });
  if (!res.ok) {
    let message = res.statusText;
    try {
      message = ((await res.json()) as {error?: string}).error ?? message;
    } catch {
      /* not json */
    }
    throw new ApiError(res.status, message);
  }
  return (await res.json()) as T;
}

export const api = {
  health: () => get<IndexerHealth>('/health'),
  state: () => get<ArenaState>('/state'),
  stats: () => get<Record<string, string | number>>('/stats'),
  tokens: (params: {sort?: string; q?: string; limit?: number; graduated?: boolean} = {}) => {
    const u = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') u.set(k, String(v));
    return get<TokenSummary[]>(`/tokens?${u}`);
  },
  token: (address: string) => get<{token: TokenSummary; curve: CurveSnapshot}>(`/tokens/${address}`),
  trades: (address: string, limit = 50) => get<Trade[]>(`/tokens/${address}/trades?limit=${limit}`),
  candles: (address: string, interval: number, from: number, to: number) =>
    get<Candle[]>(`/tokens/${address}/candles?interval=${interval}&from=${from}&to=${to}`),
  period: (k?: number) => get<PeriodView>(k === undefined ? '/arena/period' : `/arena/period/${k}`),
  draws: (limit = 20) => get<DrawRecord[]>(`/arena/draws?limit=${limit}`),
  leaderboard: (window: 'period' | 'all' = 'all') => get<LeaderboardRow[]>(`/leaderboard?window=${window}`),
  creator: (address: string) => get<CreatorView>(`/creator/${address}`),
};
